/**
 * Token management utilities with explicit cookie handling for both client-side and server-side rendering (SSR).
 * Exposes on ensureNonExpiredAccessToken function to validate and refresh access tokens,
 *
 * NOTE: For production applications, consider using a proper cookie parsing library
 * like 'cookie', 'set-cookie-parser', or 'tough-cookie' for robust Set-Cookie header parsing
 * when dealing with complex cookie values that may contain commas or other special characters.
 */

import {
  ACCESS_TOKEN_COOKIE_NAME,
  REFRESH_TOKEN_COOKIE_NAME,
  REFRESH_TOKEN_EXISTS_COOKIE_NAME,
} from '../constants/Constants';
import { jwtDecode } from '../jwt/JwtDecoder';
import Cookies from 'js-cookie';

/**
 * Interface for managing Set-Cookie headers during SSR
 */
export interface ICookieHeadersResult {
  headers: string[];
  addHeader: (cookieString: string) => void;
}

/**
 * Creates a cookie headers manager for SSR requests
 * @returns {ICookieHeadersResult} Object with headers array and addHeader function
 */
export const createCookieHeadersManager = (): ICookieHeadersResult => {
  const headers: string[] = [];

  return {
    headers,
    addHeader: (cookieString: string) => {
      headers.push(cookieString);
    },
  };
};

/**
 * Interface for SSR context containing request object
 */
interface ISsrContext {
  req?: {
    cookies?: Record<string, string>;
  };
}

/**
 * Gets the access token from either SSR context or client-side cookies.
 * @param {ISsrContext | null} ssrContext - SSR context { req } or null for client.
 * @returns {string | undefined} The access token or undefined.
 */
export const getAccessToken = (ssrContext?: ISsrContext | null): string | undefined => {
  if (ssrContext?.req) {
    // SSR
    return ssrContext.req.cookies?.[ACCESS_TOKEN_COOKIE_NAME];
  } else if (typeof window !== 'undefined') {
    // Client-side
    return Cookies.get(ACCESS_TOKEN_COOKIE_NAME);
  }
  return undefined;
};

/**
 * Parse JWT to check if token is expiring soon
 * @param {string} token - The JWT token to check
 * @param {number} bufferSeconds - Buffer time in seconds before considering token as expiring (default: 60)
 * @returns {boolean} True if token is expiring soon or invalid, false otherwise
 */
export const isTokenExpiringSoon = (token: string, bufferSeconds: number = 60): boolean => {
  try {
    const decodedToken = jwtDecode(token);
    if (!decodedToken || !decodedToken.exp) {
      return true;
    }
    const expirationTime = decodedToken.exp * 1000;
    const now = Date.now();
    const timeUntilExpiry = expirationTime - now;
    return timeUntilExpiry <= bufferSeconds * 1000;
  } catch {
    return true;
  }
};

/**
 * Interface for token refresh request options
 */
interface ITokenRefreshRequestOptions extends RequestInit {
  headers: Record<string, string>;
}

/**
 * @deprecated Use ensureNonExpiredAccessToken instead. This function will be removed in a future version.
 * Ensures access token is NOT EXPIRED and refreshes it if expired or close to expiration.
 *
 * !IMPORTANT: this function doesn't actually check the "validity" of the token (e.g. against JWKS).
 *
 * @param {string} authServiceUrl - Full URL to the auth refresh endpoint
 * @param {string | null | undefined} accessToken - The current access token
 * @param {boolean | null | undefined} refreshTokenExists - Whether refresh token exists (em_rte)
 * @param {string | undefined} refreshToken - The refresh token (em_rt) for server-side requests
 * @param {ICookieHeadersResult | undefined} cookieHeadersManager - Optional cookie headers manager for SSR
 * @returns {Promise<string | null>} Promise resolving to valid access token or null if refresh failed
 */
export async function ensureValidAccessToken(
  authServiceUrl: string,
  accessToken?: string | null,
  refreshTokenExists?: boolean | null,
  refreshToken?: string,
  cookieHeadersManager?: ICookieHeadersResult
): Promise<string | null> {
  console.warn(
    'ensureValidAccessToken is deprecated and will be removed in a future version. ' +
      'Please use ensureNonExpiredAccessToken instead.'
  );

  return ensureNonExpiredAccessToken(
    authServiceUrl,
    accessToken,
    refreshTokenExists,
    refreshToken,
    cookieHeadersManager
  );
}

/**
 * Ensures access token is NOT EXPIRED and refreshes it if expired or close to expiration
 * Useful for both client-side and server-side rendering (SSR) scenarios.
 * Sets cookies on the server-side if refresh token is available.
 *
 * @param {string} authServiceUrl - Full URL to the auth refresh endpoint
 * @param {string | null | undefined} accessToken - The current access token
 * @param {boolean | null | undefined} refreshTokenExists - Whether refresh token exists (em_rte)
 * @param {string | undefined} refreshToken - The refresh token (em_rt) for server-side requests
 * @param {ICookieHeadersResult | undefined} cookieHeadersManager - Optional cookie headers manager for SSR
 * @returns {Promise<string | null>} Promise resolving to valid access token or null if refresh failed
 *
 * @note Server-side Set-Cookie parsing uses a best-effort approach. For production use with complex
 * cookie values, consider using a dedicated cookie parsing library or ensure your auth service
 * sends each Set-Cookie as a separate header to avoid comma-related parsing issues.
 */
export async function ensureNonExpiredAccessToken(
  authServiceUrl: string,
  accessToken?: string | null,
  refreshTokenExists?: boolean | null,
  refreshToken?: string,
  cookieHeadersManager?: ICookieHeadersResult
): Promise<string | null> {
  // If we don't have a refresh token, we can't proceed
  if (!refreshTokenExists) {
    return null;
  }

  // If we have an access token, check if it's still valid
  if (accessToken && !isTokenExpiringSoon(accessToken, 60)) {
    return accessToken;
  }

  try {
    const requestOptions: ITokenRefreshRequestOptions = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
      },
    };

    if (refreshToken) {
      // Server-side request: refresh token is available, needs to be passed manually
      requestOptions.headers = {
        ...requestOptions.headers,
        Cookie: `${REFRESH_TOKEN_COOKIE_NAME}=${refreshToken}; ${REFRESH_TOKEN_EXISTS_COOKIE_NAME}=${
          refreshTokenExists ? 1 : 0
        }`,
      };
    } else {
      // Client-side request: browser handles cookies automatically
      requestOptions.credentials = 'include';
    }

    const response = await fetch(`${authServiceUrl}/RefreshToken`, requestOptions);
    let newAccessToken: string | null = null;

    if (response.ok) {
      // Try to get set-cookie headers in a Node.js compatible way
      if (refreshToken) {
        // Server-side refresh: extract tokens from Set-Cookie headers
        let setCookieHeaders: string[] = [];

        // Try to get raw headers first (available in node-fetch and some other fetch implementations)
        try {
          // Check if headers has a raw() method (node-fetch)
          const headersWithRaw = response.headers as any;
          if (typeof headersWithRaw.raw === 'function') {
            const rawHeaders = headersWithRaw.raw();
            if (rawHeaders && rawHeaders['set-cookie']) {
              setCookieHeaders = Array.isArray(rawHeaders['set-cookie'])
                ? rawHeaders['set-cookie']
                : [rawHeaders['set-cookie']];
            }
          }
        } catch {
          // Ignore errors when trying to access raw headers
        }

        // Fallback: try to get headers as array or string if raw() didn't work
        if (setCookieHeaders.length === 0) {
          const setCookieHeader = response.headers.get('set-cookie');
          if (setCookieHeader) {
            // IMPORTANT: This simple split approach may break if cookie values contain commas
            // For production use, consider using a proper cookie parsing library like 'cookie' or 'set-cookie-parser'
            // or ensure your auth service sends each Set-Cookie as separate headers
            if (Array.isArray(setCookieHeader)) {
              setCookieHeaders = setCookieHeader;
            } else {
              // WARNING: This regex-based split is a best-effort approach but can fail
              // with complex cookie values containing commas within quoted strings
              setCookieHeaders = setCookieHeader.split(/,(?=\s*[^=]+=[^;,]+(?:;|$))/);
            }
          }
        }

        setCookieHeaders.forEach((cookieHeader) => {
          const trimmedHeader = cookieHeader.trim();

          // Add to provided cookie headers manager if available
          if (cookieHeadersManager) {
            cookieHeadersManager.addHeader(trimmedHeader);
          }

          // Parse the cookie to extract the access token value
          if (trimmedHeader.includes(`${ACCESS_TOKEN_COOKIE_NAME}=`)) {
            const match = trimmedHeader.match(new RegExp(`${ACCESS_TOKEN_COOKIE_NAME}=([^;]+)`));
            if (match && match[1]) {
              newAccessToken = match[1];
            }
          }
        });
      } else if (typeof window !== 'undefined') {
        // Client-side refresh: read updated token from cookies after response
        return Cookies.get(ACCESS_TOKEN_COOKIE_NAME) || null;
      }
    }

    return newAccessToken;
  } catch (error) {
    return null;
  }
}

/**
 * Gets refresh token existence flag from either SSR context or client-side cookies
 * @param {ISsrContext | null} ssrContext - SSR context { req } or null for client
 * @returns {boolean} True if refresh token exists, false otherwise
 */
export const getRefreshTokenExists = (ssrContext?: ISsrContext | null): boolean => {
  if (ssrContext?.req) {
    // SSR
    return ssrContext.req.cookies?.[REFRESH_TOKEN_EXISTS_COOKIE_NAME] === '1';
  } else if (typeof window !== 'undefined') {
    // Client-side
    return Cookies.get(REFRESH_TOKEN_EXISTS_COOKIE_NAME) === '1';
  }
  return false;
};

/**
 * Gets the server-side refresh token (only available in SSR context)
 * NOTE: The token is HttpOnly and cannot be accessed client-side
 * @param {ISsrContext | null} ssrContext - SSR context { req }
 * @returns {string | undefined} The refresh token or undefined
 */
export const getRefreshToken = (ssrContext?: ISsrContext | null): string | undefined => {
  if (ssrContext?.req) {
    return ssrContext.req.cookies?.[REFRESH_TOKEN_COOKIE_NAME];
  }
  return undefined;
};
